import React, { useCallback, useEffect, useMemo, useState } from "react";
import api from "../lib/api";
import { toast } from "sonner";
import { brl, fmtDate } from "../lib/format";
import {
  Package, MagnifyingGlass, ArrowClockwise, WhatsappLogo, InstagramLogo, Truck, PaperPlaneTilt, CheckCircle,
} from "@phosphor-icons/react";
import LazyImage from "../components/LazyImage";
import { useAuth } from "../context/AuthContext";

const API_BASE = process.env.REACT_APP_BACKEND_URL || "";

export default function CustomerBoxes() {
  const { user } = useAuth();
  const readOnly = !!(user?.contractor_id || user?.customer_handle);
  const [handles, setHandles] = useState([]);
  const [selected, setSelected] = useState(null);
  const [filter, setFilter] = useState("");
  const [detail, setDetail] = useState(null);
  const [loadingList, setLoadingList] = useState(true);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [shipping, setShipping] = useState(null); // order id being marked as shipped
  const [shippingAll, setShippingAll] = useState(false);
  const [shippingSelected, setShippingSelected] = useState(false);
  const [picked, setPicked] = useState({}); // { [order_id]: true }

  // Reset picks when switching handle
  useEffect(() => { setPicked({}); }, [selected]);

  const loadList = useCallback(async () => {
    setLoadingList(true);
    try {
      const { data } = await api.get("/expedition/admin/customer-boxes", { params: { _: Date.now() } });
      setHandles(data);
      // Only auto-select the first handle if nothing is picked yet (avoids photo load storm)
      setSelected(prev => prev ?? (data[0]?.handle || null));
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao carregar caixinhas");
    } finally { setLoadingList(false); }
  }, []);
  useEffect(() => { loadList(); }, [loadList]);

  const loadDetail = useCallback(async (h) => {
    if (!h) { setDetail(null); return; }
    setLoadingDetail(true);
    try {
      const { data } = await api.get(`/expedition/admin/customer-boxes/${encodeURIComponent(h)}`, { params: { _: Date.now() } });
      setDetail(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao carregar caixinha");
    } finally { setLoadingDetail(false); }
  }, []);
  useEffect(() => { loadDetail(selected); }, [selected, loadDetail]);

  const shipOrders = useCallback(async ({ order_ids, order_desc, mode /* 'one' | 'selected' | 'all' */ }) => {
    if (!selected || !order_ids?.length) return;
    const n = order_ids.length;
    const confirmText = mode === "one"
      ? `Marcar "${order_desc}" como enviado? Sairá da caixinha de ${selected}.`
      : mode === "selected"
        ? `Marcar os ${n} item(ns) SELECIONADO(S) de ${selected} como enviados?`
        : `Marcar TODOS os ${n} item(ns) de ${selected} como enviados? Eles sairão da caixinha.`;
    if (!window.confirm(confirmText)) return;
    if (mode === "one") setShipping(order_ids[0]);
    else if (mode === "selected") setShippingSelected(true);
    else setShippingAll(true);
    try {
      await api.post("/expedition/admin/customer-boxes/ship", { handle: selected, order_ids });
      toast.success(mode === "one" ? "Item marcado como enviado" : `${n} item(ns) enviado(s)`);
      setPicked({});
      await loadList();
      await loadDetail(selected);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Falha ao marcar como enviado");
    } finally { setShipping(null); setShippingAll(false); setShippingSelected(false); }
  }, [selected, loadList, loadDetail]);

  const markPaid = useCallback(async (orderId, method, orderDesc) => {
    if (!window.confirm(`Dar baixa em "${orderDesc}" como ${method === "pix" ? "Pix" : "Cartão"}?`)) return;
    try {
      await api.put(`/orders/${orderId}`, { paid: true, payment_method: method });
      toast.success(`Pagamento registrado (${method === "pix" ? "Pix" : "Cartão"}).`);
      await loadList();
      await loadDetail(selected);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Falha ao dar baixa");
    }
  }, [selected, loadList, loadDetail]);

  const togglePick = useCallback((oid) => {
    setPicked(prev => {
      const next = { ...prev };
      if (next[oid]) delete next[oid]; else next[oid] = true;
      return next;
    });
  }, []);

  const pickedIds = useMemo(() => Object.keys(picked).filter(k => picked[k]), [picked]);
  const detailOrderIds = useMemo(() => (detail?.orders || []).map(o => o.id), [detail]);
  const allPicked = detailOrderIds.length > 0 && detailOrderIds.every(id => picked[id]);
  const toggleAllPicks = useCallback(() => {
    if (allPicked) setPicked({});
    else setPicked(Object.fromEntries(detailOrderIds.map(id => [id, true])));
  }, [allPicked, detailOrderIds]);
  const pickedTotal = useMemo(() => {
    const map = new Map((detail?.orders || []).map(o => [o.id, (o.price || 0) * (o.quantity || 1)]));
    return pickedIds.reduce((s, id) => s + (map.get(id) || 0), 0);
  }, [pickedIds, detail]);

  const visibleHandles = useMemo(() => {
    const f = filter.trim().toLowerCase();
    if (!f) return handles;
    return handles.filter(h =>
      (h.handle || "").toLowerCase().includes(f) ||
      (h.customer_name || "").toLowerCase().includes(f),
    );
  }, [handles, filter]);

  const totalBoxes = handles.length;
  const totalItems = handles.reduce((s, h) => s + (h.items_count || 0), 0);
  const totalValue = handles.reduce((s, h) => s + (h.total || 0), 0);

  return (
    <div data-testid="customer-boxes-page" className="space-y-6">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="label-brutal">Expedição · Painel</p>
          <h1 className="font-heading text-3xl sm:text-5xl font-black">Caixinha de clientes</h1>
          <p className="text-brand-ink2 mt-2">Mercadorias <strong>pagas</strong> aguardando envio + <strong>a pagar</strong> em aberto, agrupadas por @arroba.</p>
        </div>
        <button onClick={() => { loadList(); if (selected) loadDetail(selected); }} className="btn-white h-11" data-testid="boxes-refresh">
          <ArrowClockwise weight="bold" size={16} /> Atualizar
        </button>
      </header>

      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        <Stat label="Caixinhas abertas" value={totalBoxes} bg="bg-brand-yellow" icon={Package} />
        <Stat label="Itens totais" value={totalItems} bg="bg-brand-mint" icon={Package} />
        <Stat label="Valor total" value={brl(totalValue)} bg="bg-brand-pink text-white" icon={Truck} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[300px_1fr] gap-4">
        {/* Sidebar */}
        <aside className="card-brutal bg-white overflow-hidden">
          <div className="p-3 border-b-2 border-brand-ink bg-brand-cream flex items-center gap-2">
            <MagnifyingGlass weight="bold" size={16} />
            <input
              data-testid="boxes-filter"
              className="input-brutal !h-9 text-sm"
              placeholder="Filtrar @ ou nome…"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
            />
          </div>
          <div className="max-h-[70vh] overflow-y-auto">
            {loadingList ? (
              <p className="p-4 text-sm">Carregando…</p>
            ) : visibleHandles.length === 0 ? (
              <p className="p-4 text-sm text-brand-ink2">Nenhuma caixinha aberta.</p>
            ) : (
              visibleHandles.map(h => {
                const active = selected === h.handle;
                return (
                  <button
                    key={h.handle}
                    data-testid={`boxes-handle-${h.handle}`}
                    onClick={() => setSelected(h.handle)}
                    className={`w-full text-left px-3 py-3 border-b-2 border-brand-ink flex items-center gap-2 ${active ? "bg-brand-pink text-white" : "hover:bg-brand-yellow"}`}
                  >
                    {h.has_customer_photo && h.customer_id ? (
                      <LazyImage
                        src={`${API_BASE}/api/customers/${h.customer_id}/photo?w=96`}
                        alt=""
                        className="w-9 h-9 rounded-full border-2 border-brand-ink object-cover flex-shrink-0"
                      />
                    ) : (
                      <span className={`w-9 h-9 rounded-full border-2 border-brand-ink inline-flex items-center justify-center font-heading font-black text-xs flex-shrink-0 ${active ? "bg-white text-brand-ink" : "bg-brand-cream"}`}>
                        {((h.customer_name || h.handle || "?")[0] || "?").toUpperCase()}
                      </span>
                    )}
                    <div className="min-w-0 flex-1">
                      <p className="font-bold text-sm truncate">{h.handle}</p>
                      <p className={`text-[11px] truncate ${active ? "text-white/80" : "text-brand-ink2"}`}>
                        {h.customer_name || "sem cadastro"}
                      </p>
                      <p className={`text-[10px] font-bold uppercase tracking-wide mt-1 ${active ? "text-white" : ""}`}>
                        {h.items_count} it · {brl(h.total || 0)}
                      </p>
                      {h.pending_items_count > 0 && (
                        <p className={`text-[10px] font-bold mt-0.5 ${active ? "text-red-100" : "text-red-700"}`}>
                          ⚠ {h.pending_items_count} a pagar · {brl(h.pending_total || 0)}
                        </p>
                      )}
                    </div>
                  </button>
                );
              })
            )}
          </div>
        </aside>

        {/* Detail */}
        <section data-testid="boxes-detail">
          {!selected ? (
            <div className="card-brutal p-10 text-center bg-brand-cream">
              <Package weight="bold" size={32} className="mx-auto mb-2" />
              <p className="font-bold">Escolha uma @arroba na lista</p>
              <p className="text-brand-ink2 text-sm">para ver o conteúdo da caixinha da cliente.</p>
            </div>
          ) : loadingDetail || !detail ? (
            <div className="card-brutal p-10 text-center font-bold">Carregando caixinha…</div>
          ) : detail.orders.length === 0 ? (
            <div className="card-brutal p-10 text-center bg-brand-mint">
              <p className="font-heading text-xl font-black">Caixinha vazia</p>
              <p className="text-brand-ink2 text-sm">Nenhum item (pago ou pendente) para {detail.handle}.</p>
            </div>
          ) : (
            <BoxContent
              detail={detail}
              onShip={shipOrders}
              onMarkPaid={markPaid}
              shipping={shipping}
              shippingAll={shippingAll}
              shippingSelected={shippingSelected}
              picked={picked}
              onTogglePick={togglePick}
              allPicked={allPicked}
              onToggleAll={toggleAllPicks}
              pickedIds={pickedIds}
              pickedTotal={pickedTotal}
              readOnly={readOnly}
            />
          )}
        </section>
      </div>
    </div>
  );
}

function BoxContent({ detail, onShip, onMarkPaid, shipping, shippingAll, shippingSelected, picked, onTogglePick, allPicked, onToggleAll, pickedIds, pickedTotal, readOnly }) {
  const cust = detail.customer || {};
  const wp = (cust.whatsapp || cust.phone_mobile || "").replace(/\D/g, "");
  const addr = [
    cust.street ? `${cust.street}${cust.number ? ", " + cust.number : ""}` : null,
    cust.complement,
    cust.neighborhood,
    cust.city && cust.state ? `${cust.city}/${cust.state}` : (cust.city || cust.state),
    cust.cep ? `CEP ${cust.cep}` : null,
  ].filter(Boolean).join(" · ");
  // "Enviar tudo" / "Enviar selecionados" ship PAID items only — unpaid items
  // must first get Pix/Cartão baixa via the per-card buttons.
  const paidIds = detail.orders.filter(o => o.paid).map(o => o.id);
  const paidPickedIds = pickedIds.filter(id => paidIds.includes(id));
  const hasPicks = paidPickedIds.length > 0;

  return (
    <div className="space-y-4">
      <div className="card-brutal p-4 bg-brand-yellow">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="label-brutal">Caixinha de</p>
            <h2 className="font-heading text-2xl font-black">{detail.handle}</h2>
            <p className="text-sm">
              <strong>{cust.full_name || "(sem cadastro)"}</strong>
              {" · "}
              <span className="badge-brutal bg-white">{detail.items_count} item(ns)</span>
              {" · "}
              <span className="badge-brutal bg-brand-pink text-white">{brl(detail.total)}</span>
            </p>
            {detail.pending_items_count > 0 && (
              <p className="text-xs mt-1 font-bold text-red-700">
                ⚠ {detail.pending_items_count} item(ns) <u>A PAGAR</u> ({brl(detail.pending_total)}) — não entram no envio enquanto não baixar
              </p>
            )}
            {addr && <p className="text-xs mt-1">{addr}</p>}
          </div>
          {!readOnly && <div className="flex items-center gap-2 flex-wrap">
            <button
              data-testid="boxes-ship-selected"
              disabled={!hasPicks || shippingSelected}
              onClick={() => onShip({ order_ids: paidPickedIds, mode: "selected" })}
              className="btn-brutal bg-brand-mint text-brand-ink hover:bg-brand-pink hover:text-white h-10 !px-3 text-xs disabled:opacity-40 disabled:cursor-not-allowed"
              title="Enviar apenas os itens PAGOS selecionados (não pagos são ignorados)"
            >
              <CheckCircle weight="bold" size={14} />
              {shippingSelected
                ? "Enviando…"
                : hasPicks
                  ? `Enviar selecionados (${paidPickedIds.length} · ${brl(pickedTotal)})`
                  : "Enviar selecionados"}
            </button>
            <button
              data-testid="boxes-ship-all"
              disabled={shippingAll || paidIds.length === 0}
              onClick={() => onShip({ order_ids: paidIds, mode: "all" })}
              className="btn-brutal bg-brand-pink text-white hover:bg-brand-ink h-10 !px-3 text-xs disabled:opacity-40"
              title="Marcar toda a caixinha PAGA como enviada (não pagos ficam)"
            >
              <PaperPlaneTilt weight="bold" size={14} /> {shippingAll ? "Enviando…" : `Enviar pagos (${paidIds.length})`}
            </button>
            {wp && (
              <a
                data-testid="boxes-whatsapp"
                href={`https://wa.me/${wp}`}
                target="_blank"
                rel="noreferrer"
                className="btn-brutal bg-brand-mint text-brand-ink hover:bg-[#C7E5D0] h-10 !px-3 text-xs"
              >
                <WhatsappLogo weight="bold" size={14} /> WhatsApp
              </a>
            )}
            <a
              href={`https://instagram.com/${(detail.handle || "").replace(/^@/, "")}`}
              target="_blank"
              rel="noreferrer"
              className="btn-white h-10 !px-3 text-xs"
            >
              <InstagramLogo weight="bold" size={14} /> Instagram
            </a>
          </div>}
        </div>
        {!readOnly && <div className="mt-3 pt-3 border-t-2 border-brand-ink/20 flex items-center gap-2 text-xs">
          <input
            type="checkbox"
            data-testid="boxes-select-all"
            checked={allPicked}
            onChange={onToggleAll}
            className="w-4 h-4"
            aria-label="Selecionar todos os itens"
          />
          <span className="font-bold uppercase tracking-wider">
            {allPicked ? "Desmarcar todos" : "Selecionar todos"}
          </span>
        </div>}
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
        {detail.orders.map(o => {
          const isPicked = !!picked[o.id];
          const unpaid = !o.paid;
          return (
            <div
              key={o.id}
              data-testid={`box-item-${o.id}`}
              className={`card-brutal bg-white overflow-hidden flex flex-col relative ${isPicked ? "ring-4 ring-brand-pink" : ""} ${unpaid ? "ring-2 ring-red-500" : ""}`}
            >
              {unpaid && (
                <span className="absolute top-2 right-2 z-10 badge-brutal bg-red-600 text-white !text-[10px] animate-pulse" data-testid={`box-unpaid-${o.id}`}>
                  A PAGAR
                </span>
              )}
              {!readOnly && <label className="absolute top-2 left-2 z-10 bg-white border-2 border-brand-ink flex items-center justify-center w-7 h-7 cursor-pointer">
                <input
                  type="checkbox"
                  data-testid={`box-pick-${o.id}`}
                  checked={isPicked}
                  onChange={() => onTogglePick(o.id)}
                  disabled={unpaid}
                  className="w-4 h-4 disabled:opacity-30"
                  aria-label={`Selecionar ${o.product_code}`}
                  title={unpaid ? "Dê baixa (Pix/Cartão) antes de selecionar pra envio" : undefined}
                />
              </label>}
              <div
                className="aspect-square bg-brand-cream border-b-2 border-brand-ink flex items-center justify-center overflow-hidden cursor-pointer"
                onClick={() => !unpaid && !readOnly && onTogglePick(o.id)}
              >
                {o.has_photo ? (
                  <LazyImage
                    src={`${API_BASE}/api/orders/${o.id}/photo?w=400`}
                    alt={o.description}
                    className="w-full h-full"
                  />
                ) : (
                  <Package weight="bold" size={40} className="opacity-30" />
                )}
              </div>
              <div className="p-2 text-xs space-y-0.5 flex-1">
                <p className="font-bold truncate">{o.product_code}</p>
                <p className="text-brand-ink2 truncate" title={o.description}>{o.description}</p>
                <p className="flex items-center justify-between mt-1">
                  <span className="font-bold">{brl((o.price || 0) * (o.quantity || 1))}</span>
                  {o.quantity > 1 && <span className="badge-brutal bg-brand-cream !text-[10px]">×{o.quantity}</span>}
                </p>
                {o.live_name && (
                  <p className="text-[10px] text-brand-ink2 truncate">{o.live_date ? fmtDate(o.live_date) : ""} · {o.live_name}</p>
                )}
                {o.pending_shipment_id && (
                  <p className="text-[10px] mt-1"><span className="badge-brutal bg-brand-peach">Envio solicitado</span></p>
                )}
              </div>
              {readOnly ? null : unpaid ? (
                <div className="grid grid-cols-2 border-t-2 border-brand-ink">
                  <button
                    data-testid={`box-paid-pix-${o.id}`}
                    onClick={() => onMarkPaid(o.id, "pix", `${o.product_code} · ${o.description}`)}
                    className="h-9 text-[11px] font-bold bg-brand-mint text-brand-ink hover:bg-emerald-300 border-r-2 border-brand-ink"
                  >
                    Baixa Pix
                  </button>
                  <button
                    data-testid={`box-paid-cartao-${o.id}`}
                    onClick={() => onMarkPaid(o.id, "credit", `${o.product_code} · ${o.description}`)}
                    className="h-9 text-[11px] font-bold bg-brand-yellow text-brand-ink hover:bg-amber-300"
                  >
                    Baixa Cartão
                  </button>
                </div>
              ) : (
                <button
                  data-testid={`box-ship-${o.id}`}
                  disabled={shipping === o.id || shippingAll || shippingSelected}
                  onClick={() => onShip({ order_ids: [o.id], order_desc: `${o.product_code} · ${o.description}`, mode: "one" })}
                  className="btn-brutal bg-brand-mint text-brand-ink hover:bg-brand-pink hover:text-white border-t-2 !border-b-0 !border-l-0 !border-r-0 border-brand-ink !rounded-none !h-9 text-[11px] w-full disabled:opacity-40"
                >
                  <CheckCircle weight="bold" size={13} /> {shipping === o.id ? "Enviando…" : "Marcar enviado"}
                </button>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function Stat({ label, value, bg, icon: Icon }) {
  return (
    <div className={`card-brutal p-3 ${bg} flex items-center gap-3`}>
      <Icon weight="bold" size={22} />
      <div className="min-w-0">
        <p className="text-[10px] font-bold uppercase leading-tight">{label}</p>
        <p className="font-heading text-xl sm:text-2xl font-black break-words">{value}</p>
      </div>
    </div>
  );
}
